/** Tokens visuais — Agenda Klinvia (frontend-design + klinvia-tokens) */
export const AGENDA_THEME = {
  paper: '#FFFFFF',
  muted: '#F8FAFC',
  mutedAlt: '#F1F5F9',
  navy: '#0F172A',
  slate: '#64748B',
  slateMid: '#475569',
  slateFaint: '#94A3B8',
  border: 'rgba(13, 148, 136, 0.14)',
  borderSoft: '#E2E8F0',
  teal: '#0D9488',
  tealDark: '#0F766E',
  tealBright: '#14B8A6',
  tealSoft: 'rgba(13, 148, 136, 0.08)',
  tealMuted: 'rgba(13, 148, 136, 0.12)',
  primary: '#2563EB',
  primarySoft: 'rgba(37, 99, 235, 0.08)',
  gridLine: 'rgba(100, 116, 139, 0.06)',
  gridAccent: 'rgba(13, 148, 136, 0.18)',
  gridAccentStrong: 'rgba(13, 148, 136, 0.25)',
  gridAccentLight: 'rgba(13, 148, 136, 0.2)',
  gridHover: 'rgba(13, 148, 136, 0.04)',
  gridHoverDay: 'rgba(13, 148, 136, 0.03)',
  todayBg: 'rgba(13, 148, 136, 0.06)',
  todayHeaderBg: 'rgba(13, 148, 136, 0.08)',
  shadow: '0 1px 3px rgba(15, 23, 42, 0.06), 0 8px 24px rgba(15, 23, 42, 0.06)',
  dialogShadow: '0 24px 48px rgba(15, 23, 42, 0.12), 0 8px 16px rgba(15, 23, 42, 0.06)',
  radius: 12,
  radiusSm: 10,
  cancelledBorder: '#FECACA',
  cancelledBg: '#FEF2F2',
  cancelledBgSoft: '#FFFAF9',
  cancelledBgItem: '#FFF5F5',
  cancelledText: '#B91C1C',
  cancelledTextDark: '#991B1B',
  nowIndicator: '#EF4444',
  /** Expediente: azul-saúde Klinvia (teal ~8%), mesma leveza do creme anterior. */
  workingHoursBg: '#F2FBFA',
  workingHoursHover: '#E6F5F3',
  todayBadge: '#EF4444',
  /** Só propriedades visuais: `all` animava top/height e travava drag/resize na grade. */
  transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease',
} as const;

/** Nome do paciente nos cards — sans neutra (Inter), mais legível que Montserrat em tamanho pequeno. */
export const AGENDA_PATIENT_NAME_FONT =
  '"Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const agendaPatientNameTypoSx = (opts?: {
  fontSize?: string;
  color?: string;
  lineHeight?: number | string;
}) => ({
  fontFamily: AGENDA_PATIENT_NAME_FONT,
  fontWeight: 600,
  letterSpacing: '0.015em',
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale',
  fontSize: opts?.fontSize ?? '0.8125rem',
  color: opts?.color ?? AGENDA_THEME.navy,
  lineHeight: opts?.lineHeight ?? 1.28,
});

export const agendaClinicalGridBg = {
  backgroundColor: AGENDA_THEME.muted,
  backgroundImage: `
    linear-gradient(${AGENDA_THEME.gridLine} 1px, transparent 1px),
    linear-gradient(90deg, ${AGENDA_THEME.gridLine} 1px, transparent 1px)
  `,
  backgroundSize: '24px 24px',
};

export const agendaPageShellSx = (layoutExpanded: boolean, mobileFlush?: boolean) =>
  layoutExpanded
    ? {
        /** Safe area: topo no toolbar; base no card da grade (como inbox). */
        px: { xs: 0, sm: 1 },
        pt: 0,
        mt: 0,
        pb: 0,
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column' as const,
        height: '100%',
        ...(mobileFlush ? {} : { maxHeight: '100%' }),
        overflow: 'hidden',
        boxSizing: 'border-box' as const,
      }
    : {
        ...agendaClinicalGridBg,
        px: { xs: 1, sm: 1.5 },
        pt: { xs: 1, sm: 1.5 },
        pb: 2,
        minHeight: 'var(--ui-viewport-height, 100dvh)',
      };

export const agendaSurfaceCardSx = {
  borderRadius: 2,
  border: `1px solid ${AGENDA_THEME.borderSoft}`,
  boxShadow: AGENDA_THEME.shadow,
  bgcolor: AGENDA_THEME.paper,
};

export const agendaToolbarCardSx = (layoutExpanded: boolean, mobileFlush?: boolean) => ({
  ...agendaSurfaceCardSx,
  mb: layoutExpanded ? (mobileFlush ? 0.5 : 1) : 1.5,
  flexShrink: 0,
  ...(mobileFlush
    ? {
        borderRadius: 0,
        borderLeft: 'none',
        borderRight: 'none',
        borderBottom: 'none',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
        mb: 0,
      }
    : {}),
});

/** Toolbar mobile — safe area no conteúdo, alinhado ao inbox. */
export const agendaMobileToolbarCardContentSx = {
  pt: 'calc(env(safe-area-inset-top, 0px) + 6px) !important',
};

export const agendaPrimaryButtonSx = {
  bgcolor: AGENDA_THEME.teal,
  color: '#fff',
  textTransform: 'none' as const,
  fontWeight: 600,
  '&:hover': { bgcolor: AGENDA_THEME.tealDark },
};

export const agendaPrimaryIconButtonSx = {
  color: AGENDA_THEME.teal,
};

export const agendaOutlinedTodayButtonSx = {
  borderColor: AGENDA_THEME.teal,
  color: AGENDA_THEME.teal,
  textTransform: 'none' as const,
  fontWeight: 600,
  '&:hover': {
    borderColor: AGENDA_THEME.tealDark,
    bgcolor: AGENDA_THEME.tealSoft,
  },
};

export const agendaNavIconButtonSx = {
  color: AGENDA_THEME.navy,
  '&:hover': { bgcolor: AGENDA_THEME.tealSoft, color: AGENDA_THEME.tealDark },
};

export function agendaViewToggleButtonSx(active: boolean) {
  return active
    ? {
        ...agendaPrimaryButtonSx,
        borderRadius: 2,
      }
    : {
        borderColor: AGENDA_THEME.borderSoft,
        color: AGENDA_THEME.slate,
        textTransform: 'none' as const,
        fontWeight: 600,
        borderRadius: 2,
        '&:hover': {
          borderColor: AGENDA_THEME.border,
          bgcolor: AGENDA_THEME.tealSoft,
          color: AGENDA_THEME.tealDark,
        },
      };
}

export const agendaProgressSx = {
  mb: 1,
  bgcolor: AGENDA_THEME.tealSoft,
  '& .MuiLinearProgress-bar': { bgcolor: AGENDA_THEME.teal },
};

export const agendaFiltersPanelSx = {
  mt: 2,
  pt: 2,
  borderTop: `1px solid ${AGENDA_THEME.borderSoft}`,
  display: 'flex',
  gap: 2,
  flexWrap: 'wrap' as const,
  alignItems: 'center',
};

export const agendaProfessionalChipSx = {
  bgcolor: AGENDA_THEME.tealSoft,
  color: AGENDA_THEME.tealDark,
  border: `1px solid ${AGENDA_THEME.border}`,
  fontWeight: 600,
  fontSize: '0.8rem',
};

export const agendaGridTimeColumnSx = {
  borderRight: `2px solid ${AGENDA_THEME.gridAccentStrong}`,
  bgcolor: AGENDA_THEME.muted,
};

/** Coluna de horas mobile — não cortar rótulos que cruzam a linha da grade. */
export const agendaGridTimeColumnMobileSx = {
  borderRight: `1px solid ${AGENDA_THEME.borderSoft}`,
  bgcolor: AGENDA_THEME.paper,
  overflow: 'visible' as const,
  flexShrink: 0,
};

export const agendaGridTimeHeaderSx = {
  height: 56,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderBottom: `2px solid ${AGENDA_THEME.gridAccentStrong}`,
  position: 'sticky' as const,
  top: 0,
  zIndex: 6,
  bgcolor: AGENDA_THEME.muted,
};

export const agendaGridTimeHeaderLabelSx = (compact?: boolean) => ({
  color: AGENDA_THEME.teal,
  fontWeight: 700,
  fontSize: compact ? '0.6rem' : '0.82rem',
  letterSpacing: 0.04,
});

/** Cabeçalho da coluna de horas — semana mobile compacta (7 dias na tela). */
export const agendaGridTimeHeaderMobileCompactSx = {
  height: 46,
  minHeight: 46,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  pr: 0.5,
  borderBottom: `1px solid ${AGENDA_THEME.borderSoft}`,
  bgcolor: AGENDA_THEME.paper,
  flexShrink: 0,
};

/** Linha de hora mais suave na grade mobile densa. */
export const agendaGridLineMobileSx = {
  borderTop: `1px solid ${AGENDA_THEME.borderSoft}`,
};

/** Cabeçalho de dia — mobile compacto (letra do dia + número, estilo agenda nativa). */
export function agendaGridDayHeaderMobileCompactSx(_isToday?: boolean) {
  return {
    height: 46,
    minHeight: 46,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.2,
    pt: 0.25,
    boxSizing: 'border-box' as const,
    overflow: 'visible' as const,
    borderBottom: `1px solid ${AGENDA_THEME.borderSoft}`,
    bgcolor: AGENDA_THEME.paper,
    cursor: 'pointer',
    transition: AGENDA_THEME.transition,
    '&:hover': { bgcolor: AGENDA_THEME.muted },
  };
}

export function agendaGridDayHeaderWeekdayMobileSx(isToday: boolean) {
  return {
    fontWeight: 600,
    color: isToday ? AGENDA_THEME.primary : AGENDA_THEME.slate,
    fontSize: '0.625rem',
    lineHeight: 1.15,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  };
}

export function agendaGridDayHeaderDateMobileSx(isToday: boolean) {
  return {
    width: isToday ? 26 : 'auto',
    height: isToday ? 26 : 'auto',
    minWidth: isToday ? 26 : undefined,
    borderRadius: isToday ? '50%' : 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: isToday ? AGENDA_THEME.primary : 'transparent',
    color: isToday ? '#fff' : AGENDA_THEME.navy,
    fontSize: isToday ? '0.8125rem' : '0.75rem',
    fontWeight: isToday ? 700 : 600,
    fontVariantNumeric: 'tabular-nums' as const,
    lineHeight: 1,
  };
}

/** Rótulo de hora na coluna esquerda — logo abaixo da linha (evita clip no scroll). */
export const agendaGridHourLabelMobileCompactSx = {
  position: 'absolute' as const,
  top: 2,
  right: 2,
  left: 0,
  textAlign: 'right' as const,
  fontSize: '0.65rem',
  fontWeight: 500,
  color: AGENDA_THEME.slate,
  fontVariantNumeric: 'tabular-nums' as const,
  lineHeight: 1.15,
  whiteSpace: 'nowrap' as const,
  pointerEvents: 'none' as const,
};

/** Container dos rótulos de hora — não clipar texto no scroll vertical. */
export const agendaGridTimeLabelsHostMobileSx = {
  position: 'relative' as const,
  overflow: 'visible' as const,
};

export const agendaGridHourLabelSx = (opts?: { compact?: boolean; large?: boolean }) => ({
  fontWeight: 700,
  color: AGENDA_THEME.teal,
  fontVariantNumeric: 'tabular-nums' as const,
  lineHeight: 1.1,
  fontSize: opts?.compact ? '0.62rem' : opts?.large ? '0.86rem' : '0.72rem',
});

/** Posição do rótulo de hora cheia — afastado do canto superior esquerdo do bloco */
export const agendaGridHourLabelPositionSx = (opts?: { compact?: boolean; dayView?: boolean }) => ({
  position: 'absolute' as const,
  top: opts?.compact ? 6 : opts?.dayView ? 10 : 8,
  left: opts?.compact ? 5 : opts?.dayView ? 12 : 10,
  right: 'auto' as const,
  textAlign: 'left' as const,
});

/** Posição dos rótulos de minutos (:10, :30…) — afastados da borda direita */
export const agendaGridMinuteLabelPositionSx = (opts?: { compact?: boolean; topOffset: number }) => ({
  position: 'absolute' as const,
  top: opts?.topOffset,
  right: opts?.compact ? 5 : 10,
  left: 'auto' as const,
  textAlign: 'right' as const,
  fontVariantNumeric: 'tabular-nums' as const,
  lineHeight: 1,
});

export const agendaGridHourLineSx = (isLast: boolean) => ({
  borderBottom: isLast ? 'none' : `1px solid ${AGENDA_THEME.gridAccent}`,
});

export const agendaGridDayColumnBorderSx = {
  borderRight: `1px solid ${AGENDA_THEME.gridAccentLight}`,
};

export function agendaGridDayHeaderSx(isToday: boolean) {
  return {
    height: 56,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.25,
    borderBottom: `2px solid ${isToday ? AGENDA_THEME.todayBadge : AGENDA_THEME.gridAccentStrong}`,
    bgcolor: isToday ? AGENDA_THEME.todayHeaderBg : AGENDA_THEME.mutedAlt,
    position: 'sticky' as const,
    top: 0,
    zIndex: 5,
    cursor: 'pointer',
    transition: AGENDA_THEME.transition,
    '&:hover': { bgcolor: isToday ? AGENDA_THEME.todayBg : AGENDA_THEME.tealSoft },
  };
}

export const agendaGridDayHeaderAccentBarSx = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  right: 0,
  height: 3,
  bgcolor: AGENDA_THEME.todayBadge,
};

export function agendaGridDayHeaderWeekdaySx(isToday: boolean) {
  return {
    fontWeight: 700,
    color: isToday ? AGENDA_THEME.navy : AGENDA_THEME.slateMid,
    fontSize: '0.72rem',
    textTransform: 'capitalize' as const,
    lineHeight: 1,
  };
}

export function agendaGridDayHeaderDateCircleSx(isToday: boolean) {
  return {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: isToday ? AGENDA_THEME.todayBadge : 'transparent',
    color: isToday ? '#fff' : AGENDA_THEME.navy,
    fontSize: '0.8rem',
    fontWeight: 700,
    boxShadow: isToday ? '0 2px 6px rgba(239,68,68,0.35)' : 'none',
  };
}

export function agendaGridDayHeaderPatientsSx(isToday: boolean) {
  return {
    fontSize: '0.66rem',
    fontWeight: 600,
    lineHeight: 1,
    color: isToday ? AGENDA_THEME.navy : AGENDA_THEME.slate,
    fontVariantNumeric: 'tabular-nums' as const,
  };
}

/**
 * Vista dia agrupada por profissional: a coluna de horários fica congelada à
 * esquerda enquanto as colunas de profissionais rolam na horizontal.
 */
export const agendaStickyTimeColumnSx = {
  ...agendaGridTimeColumnSx,
  position: 'sticky' as const,
  left: 0,
  zIndex: 6,
};

/** Canto superior esquerdo (cruzamento das duas áreas congeladas). */
export const agendaStickyTimeHeaderSx = {
  ...agendaGridTimeHeaderSx,
  zIndex: 8,
};

/** Cabeçalho da coluna de um profissional — congelado no topo da grade. */
export function agendaProfessionalColumnHeaderSx(active: boolean) {
  return {
    height: 56,
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    px: 1,
    minWidth: 0,
    borderBottom: `2px solid ${AGENDA_THEME.gridAccentStrong}`,
    bgcolor: active ? AGENDA_THEME.todayHeaderBg : AGENDA_THEME.mutedAlt,
    position: 'sticky' as const,
    top: 0,
    zIndex: 5,
    cursor: 'pointer',
    transition: AGENDA_THEME.transition,
    '&:hover': { bgcolor: AGENDA_THEME.tealSoft },
  };
}

export const agendaGridSlotAreaSx = {
  position: 'relative' as const,
  cursor: 'pointer',
  bgcolor: AGENDA_THEME.workingHoursBg,
  transition: 'background-color 0.15s',
  '&:hover': { bgcolor: AGENDA_THEME.workingHoursHover },
};

export const agendaGridSlotAreaDaySx = agendaGridSlotAreaSx;

export const agendaGridLineSx = {
  borderTop: `1px solid ${AGENDA_THEME.gridAccent}`,
};

export const agendaGridHalfHourLineSx = {
  borderTop: '1px dashed rgba(148, 163, 184, 0.3)',
};

export const agendaNowIndicatorLineSx = {
  height: 2,
  bgcolor: AGENDA_THEME.nowIndicator,
};

export const agendaNowIndicatorDotSx = {
  position: 'absolute' as const,
  left: -4,
  top: -4,
  width: 10,
  height: 10,
  borderRadius: '50%',
  bgcolor: AGENDA_THEME.nowIndicator,
};

/** Ordem na coluna da grade: faixa fechada < hint de slot < consulta < linha “agora” / cabeçalho. */
export const AGENDA_GRID_Z = {
  unavailableBand: 1,
  slotHover: 2,
  appointment: 3,
  appointmentHoverStacked: 4,
  nowIndicator: 5,
  appointmentHover: 10,
} as const;

export const agendaUnavailableBandSx = {
  position: 'absolute' as const,
  left: 0,
  right: 0,
  bgcolor: '#EEF1F5',
  backgroundImage:
    'repeating-linear-gradient(45deg, rgba(148,163,184,0.28) 0, rgba(148,163,184,0.28) 5px, transparent 5px, transparent 11px)',
  pointerEvents: 'none' as const,
  zIndex: AGENDA_GRID_Z.unavailableBand,
};

export const agendaCancelledDockOuterSx = {
  flexShrink: 0,
  mt: 0.5,
  borderTop: `1px solid ${AGENDA_THEME.cancelledBorder}`,
  boxShadow: 'inset 0 1px 0 rgba(254,202,202,0.9)',
};

export const agendaCancelledDockInnerSx = {
  bgcolor: AGENDA_THEME.cancelledBgSoft,
  px: { xs: 0.75, sm: 1 },
  py: 0.75,
};

export const agendaCancelledSectionSx = {
  borderTop: `2px dashed ${AGENDA_THEME.cancelledBorder}`,
  bgcolor: AGENDA_THEME.cancelledBg,
  px: { xs: 1, sm: 1.5 },
  py: 1,
  flexShrink: 0,
};

export const agendaCancelledTitleSx = {
  fontWeight: 700,
  color: AGENDA_THEME.cancelledText,
  display: 'block',
  mb: 0.75,
  fontSize: '0.72rem',
};

export function agendaMiniCalendarDayCellSx(
  isToday: boolean,
  hasAppts: boolean,
  compact: boolean,
  isSelected?: boolean,
) {
  const cellH = compact ? 21 : 24;
  const fs = compact ? '0.62rem' : '0.7rem';
  const highlighted = isToday || !!isSelected;
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: cellH,
    fontSize: fs,
    cursor: 'pointer',
    borderRadius: 0.65,
    position: 'relative' as const,
    bgcolor: highlighted ? AGENDA_THEME.teal : 'transparent',
    color: highlighted ? '#fff' : AGENDA_THEME.navy,
    fontWeight: highlighted ? 700 : 400,
    transition: AGENDA_THEME.transition,
    '&:hover': {
      bgcolor: highlighted ? AGENDA_THEME.tealDark : AGENDA_THEME.tealSoft,
    },
    ...(hasAppts && !highlighted
      ? {
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0.35,
            width: 4,
            height: 4,
            borderRadius: '50%',
            bgcolor: AGENDA_THEME.teal,
          },
        }
      : {}),
  };
}

export function agendaMonthWeekdayHeaderSx() {
  return {
    p: 1.5,
    textAlign: 'center' as const,
    fontWeight: 700,
    color: AGENDA_THEME.navy,
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
    borderBottom: `2px solid ${AGENDA_THEME.tealMuted}`,
    bgcolor: AGENDA_THEME.muted,
    borderRadius: 1,
  };
}

export function agendaMonthDayCellSx(isToday: boolean) {
  return {
    minHeight: 100,
    p: 1,
    border: `1px solid ${AGENDA_THEME.mutedAlt}`,
    borderRadius: 2,
    cursor: 'pointer',
    transition: AGENDA_THEME.transition,
    bgcolor: isToday ? AGENDA_THEME.todayBg : AGENDA_THEME.paper,
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: AGENDA_THEME.shadow,
      borderColor: AGENDA_THEME.border,
    },
  };
}

export const agendaDayViewHeaderSx = {
  px: 1.5,
  py: 1,
  borderBottom: `2px solid ${AGENDA_THEME.gridAccentStrong}`,
  bgcolor: AGENDA_THEME.todayHeaderBg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap' as const,
  gap: 0.75,
  flexShrink: 0,
};

/** Toolbar da agenda em telas pequenas: data + Dia/Semana/Mês sempre visíveis. */
export const agendaMobileToolbarSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 0.65,
  width: '100%',
};

export const agendaMobileToolbarDateRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  width: '100%',
  minWidth: 0,
};

/** Data + Dia/Semana/Mês na mesma linha (mobile). */
export const agendaMobileToolbarNavRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.25,
  width: '100%',
  minWidth: 0,
  flexWrap: 'nowrap' as const,
};

/** Painéis expansíveis abaixo da toolbar mobile (data, profissionais na grade). */
export const agendaMobileToolbarExpandPanelSx = {
  borderTop: `1px solid ${AGENDA_THEME.borderSoft}`,
  pt: 0.85,
  pb: 0.5,
  mt: 0.15,
};

/** @deprecated alias — use agendaMobileToolbarExpandPanelSx */
export const agendaMobileDatePickerPanelSx = agendaMobileToolbarExpandPanelSx;

export const agendaMobileProfFilterListSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 0.45,
  maxHeight: 'min(42vh, 320px)',
  overflowY: 'auto' as const,
  WebkitOverflowScrolling: 'touch' as const,
  pr: 0.25,
  mr: -0.25,
};

export function agendaMobileProfFilterCardSx(selected: boolean) {
  return {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 0.75,
    width: '100%',
    minHeight: 48,
    p: 0.85,
    borderRadius: 1.5,
    border: `1px solid ${selected ? AGENDA_THEME.tealMuted : AGENDA_THEME.borderSoft}`,
    bgcolor: selected ? AGENDA_THEME.tealSoft : AGENDA_THEME.paper,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left' as const,
    transition: AGENDA_THEME.transition,
    '&:active': { opacity: 0.88 },
  };
}

export function agendaMobileDatePickerMonthChipSx(active: boolean) {
  return {
    flexShrink: 0,
    minWidth: 0,
    px: 1.1,
    py: 0.35,
    borderRadius: 999,
    fontSize: '0.68rem',
    fontWeight: 700,
    lineHeight: 1.2,
    cursor: 'pointer',
    border: `1px solid ${active ? AGENDA_THEME.teal : AGENDA_THEME.borderSoft}`,
    bgcolor: active ? AGENDA_THEME.tealSoft : AGENDA_THEME.paper,
    color: active ? AGENDA_THEME.tealDark : AGENDA_THEME.navy,
    transition: AGENDA_THEME.transition,
    '&:active': { opacity: 0.85 },
  };
}

export const agendaDialogPaperSx = (isMobile: boolean) => ({
  borderRadius: isMobile ? 0 : 4,
  border: isMobile ? 'none' : `1px solid ${AGENDA_THEME.borderSoft}`,
  boxShadow: AGENDA_THEME.dialogShadow,
  bgcolor: AGENDA_THEME.paper,
});
